import { type BoxProps, Flex, Separator, Text } from '@chakra-ui/react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import { useSettingsState } from '~/atoms/SettingsState'
import { useUserState } from '~/atoms/UserState'
import { TextButtonVariants } from '~/components/shared/componentStyles/TextButton.style'
import { DropdownTextButton } from '~/components/shared/Dropdown/DropdownTextButton'
import { MenuPlacement } from '~/components/shared/Dropdown/Menu'
import { MenuItem, MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { useAnalytics } from '~/hooks/useAnalytics'
import { QueueAgentStatus, useCallQueue } from '~/hooks/useCallQueue'
import { useQueueAlerts } from '~/hooks/useQueueToasts'
import { useQueueLogout } from '~/queries/queueQueries'
import Logger from '~/services/Logger'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'

import { ApiError, ExtensionInstance } from '@mangovoice/types'

import { useExtensionSelectorStyles } from './componentStyles/ExtensionSelector.style'

const logger = new Logger('ExtensionSelector')

interface ExtensionSelectorProps {
  hasActiveCall: boolean
  layoutProps?: BoxProps
}

const UNLINK_DEVICE_WARNING_KEY = 'unlinkDeviceWarning'

export const ExtensionSelector = ({
  hasActiveCall,
  layoutProps,
}: ExtensionSelectorProps) => {
  const { t } = useTranslation()
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)

  const {
    pbx,
    currentUserExtension,
    currentUserExtensionId,
    pbxExtensions,
    setCurrentUserExtension,
    pbxId,
  } = useUserState()
  const { setWebPhoneEnabled } = useSettingsState()
  const {
    confirmModal: [, setConfirmModalState],
    callInProgressModal: [, setCallInProgressModalState],
  } = useModalState()
  const { updateStatus, currentAgentStatus } = useCallQueue()
  const { showQueueUpdateGenericErrorModal } = useQueueAlerts()
  const queueLogout = useQueueLogout({
    extensionId: currentUserExtensionId,
    onQuerySuccess: () => {
      if (currentAgentStatus === QueueAgentStatus.InQueue) {
        void updateStatus(QueueAgentStatus.OutOfQueue)
      }
    },
  })
  const styles = useExtensionSelectorStyles()

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    justifyContent: styles.container?.justifyContent,
    alignItems: styles.container?.alignItems,
    gap: styles.container?.gap,
  }

  const headerProps = {
    display: styles.header?.display,
    alignItems: styles.header?.alignItems,
    gap: styles.header?.gap,
  }

  const extensionBadgeProps = {
    display: styles.extensionBadge?.display,
    justifyContent: styles.extensionBadge?.justifyContent,
    alignItems: styles.extensionBadge?.alignItems,
    gap: styles.extensionBadge?.gap,
  }

  const [extDropdownOpen, setExtDropdownOpen] = useState<boolean>(false)
  const [_hovered, setHovered] = useState(false)

  const showUnlinkDeviceWarning = useCallback(
    (ext: ExtensionInstance) => {
      setConfirmModalState({
        title: t('extensionSelector.confirmationModal.title'),
        message: (
          <Flex flexDirection="column" gap=".5rem" alignItems="center">
            <Text css={{ fontSize: '1rem', fontWeight: '400' }}>
              {t('extensionSelector.confirmationModal.message', {
                extensionName: currentUserExtension?.name,
              })}
            </Text>
          </Flex>
        ),
        confirmText: t('extensionSelector.confirmationModal.confirmText'),
        cancelText: t('extensionSelector.confirmationModal.cancelText'),
        closeAfterConfirm: true,
        dontShowAgainKey: UNLINK_DEVICE_WARNING_KEY,
        onConfirm: () => {
          track(WebPhoneAnalyticsEvents.ExtSwitched)
          setCurrentUserExtension(pbxId, ext)
        },
      })
    },
    [setCurrentUserExtension, currentUserExtension],
  )

  const handleSelect = useCallback(
    (ext: ExtensionInstance) => {
      if (ext.id === currentUserExtension?.id) {
        return
      }
      if (hasActiveCall) {
        setCallInProgressModalState({ open: true })
      } else {
        showUnlinkDeviceWarning(ext)
      }
    },
    [hasActiveCall, setCurrentUserExtension, showUnlinkDeviceWarning],
  )

  const handleDeactivateSelect = useCallback(() => {
    if (hasActiveCall) {
      setCallInProgressModalState({ open: true })
    } else {
      track(WebPhoneAnalyticsEvents.Deactivated)
      if (currentUserExtension) {
        queueLogout().catch((error: unknown) => {
          // 404 = extension isn't in any call queue, so there is nothing to
          // log out of.
          if ((error as ApiError)?.status === 404) {
            logger.info(
              'Queue logout skipped on deactivate: extension not in a call queue',
            )
            return
          }
          logger.error('Queue logout failed on web phone deactivate', null, {
            error,
          })
          showQueueUpdateGenericErrorModal()
        })
      }
      setCurrentUserExtension(pbxId, undefined)
      setWebPhoneEnabled(false)
    }
  }, [hasActiveCall, setWebPhoneEnabled])

  const handleExtMenuOpenChange = () => {
    setExtDropdownOpen(!extDropdownOpen)
    return !extDropdownOpen
  }

  const extMenuItems = useMemo(() => {
    const extensions =
      pbxExtensions?.map((ext) => {
        return {
          value: ext,
          content: (
            <MenuListItem
              primaryText={ext.name}
              rightContent={<ExtensionBadge extension={ext.number} />}
            />
          ),
          onSelect: () => handleSelect(ext),
        } as MenuItem<ExtensionInstance>
      }) ?? []

    extensions.push({
      value: {
        name: t('extensionSelector.none'),
        number: '',
      } as ExtensionInstance,
      content: (
        <MenuListItem
          primaryText={t('extensionSelector.deactivateWebPhone')}
          isDestructive
        />
      ),
      onSelect: handleDeactivateSelect,
    })

    return extensions
  }, [pbxExtensions, handleSelect, handleDeactivateSelect])

  return (
    <Flex h={layoutProps?.h} css={styles.container} {...containerProps}>
      <Flex
        css={styles.extensionBadge}
        {...extensionBadgeProps}
        border="2px solid var(--chakra-colors-border-emphasized)"
      >
        <Text
          css={styles.extensionBadgeText}
          color="var(--chakra-colors-fg-default)"
        >
          {currentUserExtension?.number}
        </Text>
      </Flex>
      <DropdownTextButton
        placeholder={currentUserExtension?.name}
        items={extMenuItems}
        rightIcon={extDropdownOpen ? ChevronUp : ChevronDown}
        onMenuOpenChange={handleExtMenuOpenChange}
        menuOptions={{
          width: '20rem',
          placement: MenuPlacement.BottomRight,
        }}
        variant={TextButtonVariants.Gray_1300}
        header={
          <Flex css={styles.header} {...headerProps}>
            <Text css={styles.headerText}>
              {t('extensionSelector.selectExtension')}
            </Text>
            <Separator width={'100%'} size={'sm'} variant={'solid'} />
            <Flex gap={1}>
              <Text css={styles.headerText} fontWeight={300}>
                {pbx?.name}
              </Text>
            </Flex>
          </Flex>
        }
        hoverChanged={(hovered) => {
          setHovered(hovered)
        }}
        rightIconProps={{
          w: '12px',
          h: '7px',
          flexShrink: '0',
        }}
      />
    </Flex>
  )
}
